/**
 * @file src/utils/identityGuard.js
 * @description One page, one identity — the token a request carries must be
 * the person the page believes it is.
 *
 * 2026-09-05: a tab signed in as one reviewer sent a withdrawal as another
 * person, because two tokens lived in the browser (this tab's session token
 * and another tab's remembered token) and the store the API client read
 * flipped underneath it. The storage fix (a tab prefers its own token) closed
 * the cause; this is the defence in depth (platform law AU-TOKEN, fail
 * closed): before any request leaves, the token's subject is compared with
 * the user id the app stamped when it loaded the profile. A mismatch never
 * leaves the browser.
 */

export const tokenSubject = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const sub = payload?.sub ?? payload?.userId ?? payload?.id ?? null;
    return sub == null ? null : String(sub);
  } catch {
    return null;
  }
};

/** True when both are known and disagree. Unknown on either side is not a mismatch. */
export const identityMismatch = (token, expectedUserId) => {
  const sub = tokenSubject(token);
  const expected = expectedUserId == null ? '' : String(expectedUserId);
  if (!sub || !expected) return false;
  return sub !== expected;
};

export class IdentityMismatchError extends Error {
  constructor(sub, expected) {
    super('This session belongs to a different account. Sign in again.');
    this.name = 'IdentityMismatchError';
    this.code = 'AUTH.IDENTITY_MISMATCH';
    this.detail = { tokenSubject: sub, expectedUserId: expected };
  }
}
